import styled, { keyframes, css } from "styled-components";
import { COLORS, THEME_FALLBACKS, GREEN_CSS_VARS } from "../../common/colors";

// ═══════════════════════════════════════════════════════════════════════════════
// YOG — DESIGN TOKENS
// Emerald/green wellness palette — distinct from Rogyog's indigo/violet
// Design pattern mirrors Rogyog exactly (PillRow, inline collapsible search,
// Eyebrow/Title/Desc header, DesktopThumbSection, etc.)
// ═══════════════════════════════════════════════════════════════════════════════

// ─── Keyframes ─────────────────────────────────────────────────────────────────

export const kFadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0);     }
`;

export const kScaleIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to   { opacity: 1; transform: scale(1);    }
`;

export const kFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

export const kShimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

export const kPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0   ${COLORS.primaryGlow}; }
  50%       { box-shadow: 0 0 0 7px rgba(61,107,94,0);    }
`;

// ─── Motion guard (respects prefers-reduced-motion) ──────────────────────────
const motion = (animCss) => css`
  @media (prefers-reduced-motion: no-preference) { ${animCss} }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION ROOT — design token definitions
// ═══════════════════════════════════════════════════════════════════════════════

export const Section = styled.section`
  /* ── Palette — sourced from centralized color tokens ── */
  ${GREEN_CSS_VARS}

  /* Background: warm cream/parchment — matches the hero typewriter section palette */
  --c-bg-from: #FAF7F2;
  --c-bg-to:   #F2EBE0;

  /* text color from theme with brand fallback */
  --c-text: ${({ theme }) => theme.TextColorBlack ?? THEME_FALLBACKS.TextColorBlack};

  /* ── Radii ── */
  --r-xs: 0.4rem;
  --r-sm: 0.65rem;
  --r:    1rem;
  --r-lg: 1.4rem;
  --r-xl: 2rem;

  /* ── Shadows ── */
  --s-xs: 0 1px 4px  rgba(0,0,0,0.06);
  --s-sm: 0 2px 12px rgba(0,0,0,0.07);
  --s:    0 4px 24px rgba(0,0,0,0.09);
  --s-lg: 0 8px 40px rgba(0,0,0,0.13);
  --s-xl: 0 16px 56px rgba(0,0,0,0.16);

  /* ── Spacing scale ── */
  --sp-1: 0.25rem;
  --sp-2: 0.5rem;
  --sp-3: 0.75rem;
  --sp-4: 1rem;
  --sp-5: 1.25rem;
  --sp-6: 1.5rem;
  --sp-8: 2rem;
  --sp-10: 2.5rem;
  --sp-12: 3rem;

  /* ── Transitions ── */
  --t-fast:  0.15s ease;
  --t:       0.22s ease;
  --t-slow:  0.38s ease;

  /* ── Root layout ── */
  position: relative;
  background: linear-gradient(170deg, var(--c-bg-from) 0%, var(--c-bg-to) 100%);
  /* NO overflow:hidden — would clip the waves */
`;
Section.displayName = "Section";

// ─── Decorative blobs ─────────────────────────────────────────────────────────
export const Blob = styled.span`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;

  &.g1 {
    width: 520px; height: 520px;
    background: radial-gradient(circle, ${COLORS.primary} 0%, transparent 70%);
    opacity: 0.09;
    top: -160px; left: -100px;
    filter: blur(70px);
  }
  &.g2 {
    width: 360px; height: 360px;
    background: radial-gradient(circle, ${COLORS.primaryLight} 0%, transparent 70%);
    opacity: 0.08;
    bottom: 80px; right: -80px;
    filter: blur(65px);
  }
  &.g3 {
    width: 240px; height: 240px;
    background: radial-gradient(circle, ${COLORS.accent} 0%, transparent 70%);
    opacity: 0.07;
    top: 40%; left: 40%;
    filter: blur(55px);
  }

  @media (max-width: 48em) {
    &.g1 { width: 260px; height: 260px; }
    &.g2 { width: 180px; height: 180px; }
    &.g3 { display: none; }
  }
`;
Blob.displayName = "Blob";

// ─── Inner content wrapper ────────────────────────────────────────────────────
export const Inner = styled.div`
  position: relative;
  z-index: 1;
  overflow-x: hidden;
  padding: var(--sp-8) var(--sp-4) var(--sp-12);

  @media (min-width: 48em)  { padding: var(--sp-10) 6%   var(--sp-12); }
  @media (min-width: 64em)  { padding: var(--sp-12) 7%   5rem; }
  @media (min-width: 80em)  { padding: var(--sp-12) 8%   5rem; }
`;
Inner.displayName = "Inner";

// ═══════════════════════════════════════════════════════════════════════════════
// HEADER — matches Rogyog's PageHeader / Eyebrow / Title / Desc
// ═══════════════════════════════════════════════════════════════════════════════

export const PageHeader = styled.header`
  text-align: center;
  margin-bottom: 26px;
  margin-top: -12px;
  ${motion(css`animation: ${kFadeUp} 0.65s ease both;`)}
`;
PageHeader.displayName = "PageHeader";

export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #3d6b4f;
  margin-bottom: 10px;
`;
Eyebrow.displayName = "Eyebrow";

export const Title = styled.h2`
  font-size: clamp(1.7rem, 3.6vw, 2.3rem);
  font-weight: 800;
  color: #2e1f0f;
  margin: 0 0 10px;
  line-height: 1.15;
  letter-spacing: -0.02em;
`;
Title.displayName = "Title";

export const Desc = styled.p`
  font-size: clamp(0.85rem, 2.2vw, 0.95rem);
  color: var(--c-text);
  width: 100%; max-width: 520px;
  margin: 0 auto;
  padding: 0 var(--sp-4);
  box-sizing: border-box;
  line-height: 1.85;
  opacity: 0.72;
  @media (min-width: 48em) { padding: 0; }
`;
Desc.displayName = "Desc";

// ─── Hero trust badges — matches Rogyog's BadgeRow/BadgeCard exactly ────────

export const BadgeRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  max-width: 760px;
  margin: 22px auto 26px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;
BadgeRow.displayName = "BadgeRow";

export const BadgeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(46, 31, 15, 0.08);
  background: #fff;
  text-align: left;
`;
BadgeCard.displayName = "BadgeCard";

export const BadgeIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(61, 107, 79, 0.1);
  color: #3d6b4f;

  svg { width: 16px; height: 16px; }
`;
BadgeIconWrap.displayName = "BadgeIconWrap";

export const BadgeLabel = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.3;
  color: #2e1f0f;
`;
BadgeLabel.displayName = "BadgeLabel";

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY PILLS — matches Rogyog's PillRow / Pill exactly
// ═══════════════════════════════════════════════════════════════════════════════

export const PillRow = styled.div`
  display: flex;
  gap: var(--sp-2);
  overflow-x: auto;
  padding: var(--sp-1) var(--sp-1) var(--sp-3);
  margin-bottom: var(--sp-6);
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }

  /* fade-out edges hint scrollability */
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);
          mask-image: linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%);

  /* justify on desktop */
  @media (min-width: 64em) {
    justify-content: center;
    -webkit-mask-image: none;
            mask-image: none;
    flex-wrap: wrap;
  }
`;
PillRow.displayName = "PillRow";

export const Pill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 0.6rem 1.25rem;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 700;
  white-space: nowrap;
  min-height: 44px;
  transition: background 150ms ease, color 150ms ease, border-color 150ms ease,
              transform var(--t-fast);
  border: 1.5px solid ${({ $active }) => ($active ? "#2c5940" : "rgba(46,31,15,0.14)")};
  background: ${({ $active }) => ($active ? "#2c5940" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#fff" : "#3d3226")};

  svg {
    flex: 0 0 auto;
    color: ${({ $active, $iconColor }) => ($active ? "#fff" : $iconColor || "#3d6b4f")};
  }

  &:hover:not(:disabled) {
    border-color: #2c5940;
    transform: translateY(-1px);
  }
  &:active { transform: translateY(0) scale(0.98); }
  &:focus-visible {
    outline: 3px solid rgba(61, 107, 79, 0.35);
    outline-offset: 2px;
  }

  ${motion(css`
    animation: ${kFadeUp} 0.45s ease both;
    animation-delay: ${({ $index }) => $index * 50}ms;
  `)}
`;
Pill.displayName = "Pill";

// ═══════════════════════════════════════════════════════════════════════════════
// INLINE COLLAPSIBLE SEARCH — mirrors Rogyog's pattern exactly
// ═══════════════════════════════════════════════════════════════════════════════

const kCollapseIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* Row that holds the toggle button */
export const SearchToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sp-3);
  flex-wrap: wrap;
  gap: var(--sp-2);

  @media (min-width: 48em) {
    flex-direction: column;
    align-items: stretch;
  }
`;
SearchToggleRow.displayName = "SearchToggleRow";

export const SearchToggleBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
  width: 100%;
  padding: 0.85rem 1.3rem;
  border-radius: 999px;
  border: 1.5px solid rgba(0,0,0,0.14);
  background: var(--c-white);
  color: var(--c-text);
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  min-height: 52px;
  box-shadow: 0 1px 8px rgba(0,0,0,0.06);
  transition: border-color var(--t), box-shadow var(--t), transform var(--t-fast);

  ${({ $active }) => $active && css`
    border-color: var(--c-accent);
    box-shadow: 0 0 0 3px var(--c-accent-tint), 0 2px 10px rgba(0,0,0,0.07);
  `}

  .toggle-label {
    display: flex; align-items: center; gap: 0.55rem; min-width: 0;
  }
  svg.search-icon {
    flex-shrink: 0; width: 17px; height: 17px;
    color: var(--c-accent); opacity: 0.65;
  }
  .toggle-text {
    font-size: 0.92rem; font-weight: 600;
    color: var(--c-text);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }

  svg.chevron-icon {
    flex-shrink: 0; width: 16px; height: 16px;
    color: var(--c-text); opacity: 0.55;
    transition: transform 0.3s ease;
    transform: ${({ $active }) => $active ? "rotate(180deg)" : "rotate(0deg)"};
  }

  &:hover {
    border-color: var(--c-accent);
    box-shadow: 0 0 0 3px var(--c-accent-tint), 0 2px 10px rgba(0,0,0,0.07);
    transform: translateY(-1px);
    svg.chevron-icon { color: var(--c-accent); opacity: 1; }
  }
  &:active { transform: scale(0.98); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 3px; }

  @media (min-width: 48em) {
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
    padding: 0.85rem 1.6rem;
    justify-content: center;
  }
`;
SearchToggleBtn.displayName = "SearchToggleBtn";

export const ActiveFilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--c-accent);
  background: var(--c-accent-tint);
  border: 1px solid var(--c-border-accent);
  border-radius: 999px;
  padding: 0.2rem 0.6rem;
`;
ActiveFilterBadge.displayName = "ActiveFilterBadge";

export const SearchCollapsible = styled.div`
  overflow: hidden;
  max-height: ${({ $open }) => $open ? "1200px" : "0"};
  transition: max-height ${({ $open }) => $open ? "0.42s" : "0.28s"}
              cubic-bezier(0.4, 0, 0.2, 1),
              opacity ${({ $open }) => $open ? "0.35s" : "0.2s"} ease;
  opacity: ${({ $open }) => $open ? 1 : 0};
  padding-bottom: ${({ $open }) => $open ? "0.25rem" : "0"};
`;
SearchCollapsible.displayName = "SearchCollapsible";

export const SearchPanel = styled.div`
  background: var(--c-surface);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  padding: var(--sp-5) var(--sp-4) var(--sp-6);
  box-shadow: var(--s-sm);
  margin-bottom: var(--sp-4);

  ${motion(css`animation: ${kCollapseIn} 0.32s ease both;`)}
  @media (min-width: 48em) {
    padding: var(--sp-6) var(--sp-6) var(--sp-8);
    border-radius: var(--r-xl);
  }
`;
SearchPanel.displayName = "SearchPanel";

export const SearchLabel = styled.label`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--c-accent);
  margin-bottom: var(--sp-3);
`;
SearchLabel.displayName = "SearchLabel";

export const SearchInputWrap = styled.div`
  position: relative;
  max-width: 600px;

  .search-icon {
    position: absolute;
    left: 1.05rem;
    top: 50%;
    transform: translateY(-50%);
    width: 17px;
    height: 17px;
    color: var(--c-accent);
    opacity: 0.45;
    pointer-events: none;
  }
`;
SearchInputWrap.displayName = "SearchInputWrap";

export const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.82rem 3rem 0.82rem 2.7rem;
  border-radius: 999px;
  border: 1.5px solid var(--c-border-accent);
  background: var(--c-surface-high);
  font-size: 0.92rem;
  color: var(--c-text);
  transition: border-color var(--t), box-shadow var(--t), background var(--t);
  -webkit-appearance: none;
  min-height: 48px;

  &::placeholder { opacity: 0.38; }

  &:focus {
    outline: none;
    border-color: var(--c-accent);
    background: var(--c-white);
    box-shadow: 0 0 0 3px var(--c-accent-tint), var(--s-sm);
  }

  &::-webkit-search-cancel-button { display: none; }
`;
SearchInput.displayName = "SearchInput";

export const ClearBtn = styled.button`
  position: absolute;
  right: 0.9rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.07);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 0.7rem;
  color: var(--c-text);
  transition: background var(--t-fast), transform var(--t-fast);

  &:hover { background: rgba(0,0,0,0.14); transform: translateY(-50%) scale(1.1); }
  &:active { transform: translateY(-50%) scale(0.95); }
  &:focus-visible { outline: 2px solid var(--c-accent); }
`;
ClearBtn.displayName = "ClearBtn";

export const TagSuggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: var(--sp-4);
`;
TagSuggestions.displayName = "TagSuggestions";

export const TagChip = styled.button`
  padding: 0.32rem 0.82rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  min-height: 34px;
  transition: all var(--t);
  border: 1px solid ${({ $active }) => $active ? "var(--c-accent)" : "var(--c-border-accent)"};
  background: ${({ $active }) => $active
    ? "linear-gradient(135deg, var(--c-accent), var(--c-accent-2))"
    : "var(--c-accent-tint)"};
  color: ${({ $active }) => $active ? "#fff" : "var(--c-accent)"};
  box-shadow: ${({ $active }) => $active ? `0 2px 10px ${COLORS.primaryGlow}` : "none"};

  &:hover {
    background: linear-gradient(135deg, var(--c-accent), var(--c-accent-2));
    color: #fff;
    border-color: var(--c-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${COLORS.primaryGlow};
  }
  &:active { transform: translateY(0) scale(0.97); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 2px; }
`;
TagChip.displayName = "TagChip";

// ═══════════════════════════════════════════════════════════════════════════════
// SEARCH RESULTS
// ═══════════════════════════════════════════════════════════════════════════════

export const SearchResultsWrap = styled.div`
  margin-top: var(--sp-6);
  ${motion(css`animation: ${kScaleIn} 0.3s ease both;`)}
`;
SearchResultsWrap.displayName = "SearchResultsWrap";

export const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sp-4);
  flex-wrap: wrap;
  gap: var(--sp-2);
`;
ResultsHeader.displayName = "ResultsHeader";

export const ResultCount = styled.span`
  font-size: 0.8rem;
  color: var(--c-text);
  opacity: 0.5;
  font-weight: 600;
`;
ResultCount.displayName = "ResultCount";

export const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--sp-4);

  @media (min-width: 30em) { grid-template-columns: repeat(2, 1fr); }
  @media (min-width: 56em) { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 80em) { grid-template-columns: repeat(4, 1fr); }
`;
ResultsGrid.displayName = "ResultsGrid";

export const ResultCard = styled.button`
  text-align: left;
  background: var(--c-surface);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid var(--c-border);
  border-radius: var(--r);
  box-shadow: var(--s-sm);
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  transition: transform var(--t), box-shadow var(--t), border-color var(--t);
  width: 100%;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--s-xl);
    border-color: var(--c-border-accent);
  }
  &:active { transform: translateY(-1px); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 3px; }

  ${motion(css`
    animation: ${kFadeUp} 0.4s ease both;
    animation-delay: ${({ $index }) => Math.min($index * 55, 400)}ms;
  `)}
`;
ResultCard.displayName = "ResultCard";

export const ResultThumb = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${COLORS.primaryDark};
  position: relative;
  overflow: hidden;

  img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
    transition: transform 0.4s ease;
  }
  ${ResultCard}:hover img { transform: scale(1.06); }
`;
ResultThumb.displayName = "ResultThumb";

export const ResultPlayOverlay = styled.div`
  position: absolute; inset: 0;
  background: var(--c-overlay);
  display: flex; align-items: center; justify-content: center;
  opacity: 0;
  transition: opacity var(--t);
  ${ResultCard}:hover & { opacity: 1; }

  svg {
    width: 32px; height: 32px; fill: white;
    filter: drop-shadow(0 2px 6px rgba(0,0,0,0.5));
  }
`;
ResultPlayOverlay.displayName = "ResultPlayOverlay";

export const ResultBody = styled.div`
  padding: var(--sp-3) var(--sp-4) var(--sp-4);
`;
ResultBody.displayName = "ResultBody";

export const ResultName = styled.div`
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--c-text);
  margin-bottom: var(--sp-1);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
ResultName.displayName = "ResultName";

export const ResultMeta = styled.div`
  font-size: 0.7rem;
  opacity: 0.45;
  color: var(--c-text);
  margin-bottom: var(--sp-3);
  line-height: 1.4;
`;
ResultMeta.displayName = "ResultMeta";

export const ResultTags = styled.div`
  display: flex; flex-wrap: wrap; gap: var(--sp-1);
`;
ResultTags.displayName = "ResultTags";

export const ResultTag = styled.span`
  font-size: 0.62rem;
  padding: 0.15rem 0.52rem;
  border-radius: 999px;
  background: var(--c-accent-tint);
  color: var(--c-accent);
  border: 1px solid var(--c-border-accent);
  font-weight: 600;
`;
ResultTag.displayName = "ResultTag";

export const NoResults = styled.div`
  text-align: center;
  padding: var(--sp-12) var(--sp-4);
  color: var(--c-text);
  opacity: 0.4;

  .icon { font-size: 3rem; display: block; margin-bottom: var(--sp-3); }
  p { font-size: 0.9rem; line-height: 1.6; margin: 0; }
`;
NoResults.displayName = "NoResults";

// ═══════════════════════════════════════════════════════════════════════════════
// BROWSE LAYOUT — mobile-first, matches Rogyog exactly
// ═══════════════════════════════════════════════════════════════════════════════

export const BrowseGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--sp-4);

  /* tablet: two side-by-side cards equal-width, right panel below */
  @media (min-width: 48em) {
    grid-template-columns: 1fr 1fr;
    & > :nth-child(3) { grid-column: 1 / -1; }
  }

  /* desktop: classic 3-column layout */
  @media (min-width: 64em) {
    grid-template-columns: minmax(180px, 220px) minmax(170px, 210px) 1fr;
    & > :nth-child(3) { grid-column: auto; }
  }
`;
BrowseGrid.displayName = "BrowseGrid";

// ─── Sidebar card ─────────────────────────────────────────────────────────────
export const Card = styled.aside`
  background: var(--c-white);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: var(--r-lg);
  border: 1px solid var(--c-border);
  box-shadow: var(--s-sm);
  display: flex;
  flex-direction: column;
  max-height: 420px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: var(--c-border-accent) transparent;
  &::-webkit-scrollbar       { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--c-border-accent); border-radius: 2px; }

  /* hide on small mobile — MobileVideoList takes over */
  @media (max-width: 47.99em) { display: none; }

  ${motion(css`
    animation: ${kFadeUp} 0.5s ease both;
    animation-delay: ${({ $delay }) => $delay ?? 0}ms;
  `)}
`;
Card.displayName = "Card";

export const CardHead = styled.div`
  padding: var(--sp-3) var(--sp-4);
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--c-accent);
  border-bottom: 1px solid rgba(0,0,0,0.06);
  position: sticky;
  top: 0;
  background: var(--c-surface-high);
  backdrop-filter: blur(12px);
  z-index: 2;
`;
CardHead.displayName = "CardHead";

// ─── Sub-category sidebar item ────────────────────────────────────────────────
export const SideItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 0.7rem var(--sp-4);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  background: ${({ $active }) => $active
    ? `linear-gradient(90deg, var(--c-accent-tint) 0%, ${COLORS.primaryTint} 100%)`
    : "transparent"};
  border-left: 3px solid ${({ $active }) => $active ? "var(--c-accent)" : "transparent"};
  transition: background var(--t-fast), border-left-color var(--t-fast);
  min-height: 52px;

  /* left content block */
  .side-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .side-name {
    font-size: 0.8rem;
    font-weight: ${({ $active }) => $active ? 700 : 500};
    color: ${({ $active }) => $active ? "var(--c-accent)" : "var(--c-text)"};
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color var(--t-fast);
  }

  &:hover {
    background: linear-gradient(90deg, var(--c-accent-tint) 0%, ${COLORS.primaryTint} 100%);
    border-left-color: var(--c-accent);
    .side-name { color: var(--c-accent); }
  }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: -2px; }
`;
SideItem.displayName = "SideItem";

export const Badge = styled.span`
  flex-shrink: 0;
  font-size: 0.58rem;
  font-weight: 800;
  background: ${({ $active }) => $active
    ? "var(--c-accent)"
    : "var(--c-accent-tint)"};
  color: ${({ $active }) => $active ? "#fff" : "var(--c-accent)"};
  border: 1px solid var(--c-border-accent);
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  min-width: 1.5rem;
  text-align: center;
  transition: background var(--t-fast), color var(--t-fast);
`;
Badge.displayName = "Badge";

// ─── Level chip inside sidebar (replaces inline LevelChip style) ──────────────
export const LevelBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  border-radius: 4px;
  padding: 1px 6px;
  line-height: 1.6;
  white-space: nowrap;
  background: ${({ $level }) =>
    $level === "Beginners"    ? "#e8f5e9" :
    $level === "Intermediate" ? "#fff8e1" :
    $level === "Advanced"     ? "#fce4ec" : "#f3f4f6"};
  color: ${({ $level }) =>
    $level === "Beginners"    ? "#2e7d32" :
    $level === "Intermediate" ? "#b45309" :
    $level === "Advanced"     ? "#9d174d" : "#374151"};
  border: 1px solid ${({ $level }) =>
    $level === "Beginners"    ? "#c8e6c9" :
    $level === "Intermediate" ? "#fcd34d" :
    $level === "Advanced"     ? "#fbcfe8" : "#d1d5db"};
`;
LevelBadge.displayName = "LevelBadge";

// ─── Video item in Col 2 — thumbnail + name + duration ───────────────────────
export const VideoItem = styled.button`
  width: 100%;
  text-align: left;
  padding: var(--sp-3) var(--sp-3);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  background: ${({ $active }) => $active
    ? `linear-gradient(90deg, var(--c-accent-tint) 0%, ${COLORS.primaryTint} 100%)`
    : "transparent"};
  border-left: 3px solid ${({ $active }) => $active ? "var(--c-accent)" : "transparent"};
  transition: background var(--t-fast), border-left-color var(--t-fast);
  min-height: 60px;

  &:hover {
    background: linear-gradient(90deg, var(--c-accent-tint) 0%, ${COLORS.primaryTint} 100%);
    border-left-color: var(--c-accent);
    .video-name { color: var(--c-accent); }
  }
  &:active { transform: scale(0.99); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: -2px; }

  /* thumbnail wrapper */
  .vid-thumb {
    flex-shrink: 0;
    width: 72px;
    height: 46px;
    border-radius: var(--r-sm);
    overflow: hidden;
    background: ${COLORS.primaryDark};
    position: relative;
    border: 1.5px solid ${({ $active }) => $active ? "var(--c-accent)" : "rgba(0,0,0,0.08)"};
    transition: border-color var(--t-fast);
  }
  .vid-thumb img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
    transition: transform 0.3s ease;
  }
  &:hover .vid-thumb img { transform: scale(1.06); }

  /* play overlay on active */
  .vid-play {
    position: absolute; inset: 0;
    background: rgba(0,0,0,0.38);
    display: flex; align-items: center; justify-content: center;
    opacity: ${({ $active }) => $active ? 1 : 0};
    transition: opacity var(--t-fast);
    svg { width: 14px; height: 14px; fill: #fff; }
  }
  &:hover .vid-play { opacity: 1; }

  /* text block */
  .vid-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .video-name {
    font-size: 0.76rem;
    font-weight: ${({ $active }) => $active ? 700 : 500};
    color: ${({ $active }) => $active ? "var(--c-accent)" : "var(--c-text)"};
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    transition: color var(--t-fast);
  }
  .vid-dur {
    font-size: 0.65rem;
    color: var(--c-accent);
    opacity: 0.65;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 3px;
  }
`;
VideoItem.displayName = "VideoItem";

// ─── Group header inside sidebar ──────────────────────────────────────────────
export const GroupHeader = styled.div`
  padding: 0.55rem var(--sp-4) 0.3rem;
  font-size: 0.58rem; font-weight: 800; letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--c-accent); opacity: 0.65;
  background: ${COLORS.primaryTint};
  border-top: 1px solid rgba(0,0,0,0.05);
`;
GroupHeader.displayName = "GroupHeader";

// ─── Mobile video list ────────────────────────────────────────────────────────
export const MobileVideoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);

  @media (min-width: 48em) { display: none; }
`;
MobileVideoList.displayName = "MobileVideoList";

export const MobileGroupLabel = styled.div`
  font-size: 0.65rem; font-weight: 800; letter-spacing: 0.12em;
  text-transform: uppercase; color: var(--c-accent); opacity: 0.7;
  padding: 0.5rem var(--sp-2) 0.25rem;
`;
MobileGroupLabel.displayName = "MobileGroupLabel";

export const MobileVideoItem = styled.button`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4);
  background: ${({ $active }) => $active ? "var(--c-accent-tint)" : "var(--c-surface)"};
  border: 1.5px solid ${({ $active }) => $active ? "var(--c-accent)" : "var(--c-border)"};
  border-radius: var(--r-sm);
  cursor: pointer;
  text-align: left;
  width: 100%;
  min-height: 52px;
  box-shadow: ${({ $active }) => $active ? "0 0 0 3px var(--c-accent-tint)" : "var(--s-xs)"};
  transition: all var(--t);

  &:hover { border-color: var(--c-accent); background: var(--c-accent-tint); }
  &:active { transform: scale(0.99); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 2px; }

  .info { flex: 1; min-width: 0; }
  .name {
    font-size: 0.82rem; font-weight: 600;
    color: ${({ $active }) => $active ? "var(--c-accent)" : "var(--c-text)"};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.35;
    word-break: break-word;
  }
  .dur  { font-size: 0.68rem; opacity: 0.45; margin-top: 2px; }
  .dot  {
    width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
    background: ${({ $active }) => $active ? "var(--c-accent)" : "rgba(0,0,0,0.12)"};
    transition: background var(--t);
    ${({ $active }) => $active && css`animation: ${kPulse} 2s ease infinite;`}
  }
`;
MobileVideoItem.displayName = "MobileVideoItem";

// ─── Right panel ──────────────────────────────────────────────────────────────
export const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
  min-width: 0;
  overflow: hidden;

  ${motion(css`animation: ${kScaleIn} 0.45s ease both 0.15s;`)}
`;
RightPanel.displayName = "RightPanel";

// ═══════════════════════════════════════════════════════════════════════════════
// VIDEO PLAYER
// ═══════════════════════════════════════════════════════════════════════════════

export const PlayerWrap = styled.div`
  border-radius: var(--r);
  overflow: hidden;
  background: ${COLORS.primaryDark};
  box-shadow: var(--s-xl);
  position: relative;

  &::before { content: ""; display: block; padding-top: 56.25%; }

  iframe {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    border: none;
  }

  @media (min-width: 48em) { border-radius: var(--r-lg); }
`;
PlayerWrap.displayName = "PlayerWrap";

export const PlayerMeta = styled.div`
  margin-top: var(--sp-3);
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
  flex-wrap: wrap;
  overflow: hidden;

  @media (max-width: 47.99em) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
PlayerMeta.displayName = "PlayerMeta";

export const VideoTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--c-text);
  flex: 1;
  line-height: 1.35;
  min-width: 0;
  word-break: break-word;

  @media (min-width: 48em) { font-size: 0.95rem; }
`;
VideoTitle.displayName = "VideoTitle";

export const VideoTag = styled.span`
  font-size: 0.62rem;
  padding: 0.22rem 0.65rem;
  border-radius: 999px;
  background: var(--c-accent-tint);
  color: var(--c-accent);
  border: 1px solid var(--c-border-accent);
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: 700;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;
VideoTag.displayName = "VideoTag";

export const Duration = styled.span`
  font-size: 0.72rem;
  opacity: 0.42;
  white-space: nowrap;
  align-self: center;
  font-weight: 500;
`;
Duration.displayName = "Duration";

export const EmptyState = styled.div`
  border-radius: var(--r-lg);
  min-height: 220px;
  background: var(--c-surface);
  backdrop-filter: blur(10px);
  border: 2px dashed var(--c-border-accent);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  opacity: 0.55;

  .icon { font-size: 2.8rem; }
  span  { font-size: 0.87rem; color: var(--c-text); }

  @media (min-width: 48em) { min-height: 250px; }
`;
EmptyState.displayName = "EmptyState";

// ═══════════════════════════════════════════════════════════════════════════════
// THUMBNAIL GRID — desktop only, matches Rogyog's DesktopThumbSection
// ═══════════════════════════════════════════════════════════════════════════════

export const DesktopThumbSection = styled.div`
  display: none;
  @media (min-width: 48em) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--sp-3);
  }
  @media (min-width: 56em) { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 72em) { grid-template-columns: repeat(4, 1fr); }
`;
DesktopThumbSection.displayName = "DesktopThumbSection";

export const Thumb = styled.button`
  border: 2px solid ${({ $active }) => $active ? "var(--c-accent)" : "transparent"};
  border-radius: var(--r-sm);
  overflow: hidden;
  background: var(--c-surface);
  cursor: pointer;
  padding: 0;
  text-align: left;
  width: 100%;
  box-shadow: ${({ $active }) => $active
    ? "0 0 0 4px var(--c-accent-tint), var(--s)"
    : "var(--s-xs)"};
  transition: transform var(--t), box-shadow var(--t), border-color var(--t);

  &:hover { transform: translateY(-3px); box-shadow: var(--s-lg); }
  &:active { transform: translateY(-1px) scale(0.99); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: 2px; }

  ${motion(css`
    animation: ${kFadeUp} 0.4s ease both;
    animation-delay: ${({ $index }) => Math.min($index * 50, 380)}ms;
  `)}
`;
Thumb.displayName = "Thumb";

export const ThumbImg = styled.div`
  width: 100%; aspect-ratio: 16 / 9;
  background: ${COLORS.primaryDark}; position: relative; overflow: hidden;

  img {
    width: 100%; height: 100%; object-fit: cover; display: block;
    transition: transform 0.38s ease;
  }
  ${Thumb}:hover img { transform: scale(1.08); }
`;
ThumbImg.displayName = "ThumbImg";

export const ThumbOverlay = styled.div`
  position: absolute; inset: 0;
  background: var(--c-overlay);
  display: flex; align-items: center; justify-content: center;
  opacity: ${({ $active }) => $active ? 1 : 0};
  transition: opacity var(--t);
  ${Thumb}:hover & { opacity: 1; }

  svg { width: 26px; height: 26px; fill: white; filter: drop-shadow(0 1px 4px rgba(0,0,0,0.6)); }
`;
ThumbOverlay.displayName = "ThumbOverlay";

export const ThumbName = styled.div`
  padding: 0.44rem 0.52rem 0.1rem;
  font-size: 0.67rem; font-weight: 600;
  color: var(--c-text); line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;
ThumbName.displayName = "ThumbName";

export const ThumbDur = styled.div`
  padding: 0 0.52rem 0.46rem;
  font-size: 0.6rem; opacity: 0.38; font-weight: 500;
`;
ThumbDur.displayName = "ThumbDur";

// ═══════════════════════════════════════════════════════════════════════════════
// ACCORDIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const DetailBox = styled.div`
  background: var(--c-surface);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: var(--r);
  border: 1px solid var(--c-border);
  box-shadow: var(--s-sm);
  overflow: hidden;
`;
DetailBox.displayName = "DetailBox";

export const AccordionBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--sp-4) var(--sp-5);
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--c-accent);
  gap: var(--sp-3);
  min-height: 52px;
  transition: background var(--t-fast);

  &:hover { background: var(--c-accent-tint); }
  &:focus-visible { outline: 2px solid var(--c-accent); outline-offset: -2px; }

  .chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.4rem; height: 1.4rem;
    border-radius: 50%;
    background: var(--c-accent-tint);
    font-size: 0.65rem;
    flex-shrink: 0;
    transition: transform var(--t-slow), background var(--t);
    transform: ${({ $open }) => $open ? "rotate(180deg)" : "rotate(0deg)"};
  }
  &:hover .chevron { background: var(--c-border-accent); }
`;
AccordionBtn.displayName = "AccordionBtn";

export const AccordionBody = styled.div`
  max-height: ${({ $open }) => $open ? "900px" : "0"};
  overflow: hidden;
  transition: max-height var(--t-slow) cubic-bezier(0.4, 0, 0.2, 1);
`;
AccordionBody.displayName = "AccordionBody";

export const AccordionInner = styled.div`
  padding: var(--sp-4) var(--sp-5) var(--sp-5);

  ol, ul { padding-left: 1.3rem; margin: 0; }
  li {
    font-size: 0.83rem;
    color: var(--c-text);
    line-height: 1.85;
    margin-bottom: var(--sp-2);
    opacity: 0.82;
  }
  p {
    font-size: 0.83rem;
    color: var(--c-text);
    line-height: 1.85;
    opacity: 0.82;
    margin: 0;
  }
`;
AccordionInner.displayName = "AccordionInner";

// ═══════════════════════════════════════════════════════════════════════════════
// WAVE DIVIDERS — SVG, zero layout-shift
// ═══════════════════════════════════════════════════════════════════════════════

export const WaveTopWrap = styled.div`
  width: 100%;
  overflow: hidden;
  line-height: 0;
  transform: scaleY(-1);
  margin-bottom: -1px;
  svg { display: block; width: 100%; height: auto; }
`;
WaveTopWrap.displayName = "WaveTopWrap";

export const WaveBottomWrap = styled.div`
  width: 100%;
  overflow: hidden;
  line-height: 0;
  margin-top: -1px;
  svg { display: block; width: 100%; height: auto; }
`;
WaveBottomWrap.displayName = "WaveBottomWrap";